import Autocomplete from '@mui/material/Autocomplete'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import { AppTextField } from 'src/feature/form/component/analog/AppTextField'
import { AppCheckbox } from 'src/feature/form/component/analog/AppCheckbox'
import { useB2bStore } from 'src/feature/b2b/store/b2b.store'
import { getKnownParentLevelId, OrganisationLevel } from 'src/feature/b2b/model/OrganisationLevel'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { AppCircleLoader } from 'src/component/common/analog/molecule/loader/AppCircleLoader'
import { ColorTokens } from 'src/feature/theme/model/Palette'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

type OrgOption = {
  id: string
  name: string
  dept: number
  levelType?: string
}

export type OrgLevelsSelectorProps = {
  label: string
  placeholder: string
  disabled?: boolean
  defaultValues: string[]
  onChange: (ids: string[]) => void
  organiationLevels: OrganisationLevel[]
  showOptionForAll?: boolean
}

export default function OrgLevelsSelector(props: OrgLevelsSelectorProps) {
  const b2bStore = useB2bStore()
  const { translation } = useLocalizationStore()
  const { orgSettings } = translation.pages.eLearning
  const orgLevelsState = b2bStore.getGenericFieldsState('handleLoadOrganisationLevels')
  const levelIds = new Set(props.organiationLevels.map(level => level.id))

  const mapOrganisation = (level: OrganisationLevel, dept: number): OrgOption[] => {
    return [{ id: level.id, name: level.name, dept, levelType: level.levelType }, ...props.organiationLevels
      .filter(child => getKnownParentLevelId(child, levelIds) === level.id).map(child => mapOrganisation(child, dept + 1))
      .flat()]
  }

  const defaultOptions = [...props.organiationLevels.filter(level => !getKnownParentLevelId(level, levelIds))
    .map(l => mapOrganisation(l, 1))
    .flat()]
  const options: OrgOption[] = props.showOptionForAll ? [{ id: '', name: orgSettings.selectAll(), dept: 1 }, ...defaultOptions] : defaultOptions

  if (orgLevelsState.isLoading) {
    return <AppCircleLoader isShowing={true} />
  }

  return (
    <Autocomplete
      multiple
      id="org-level-selector"
      sx={{
        '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
          color: 'text.primary',
        },
        '& .MuiChip-root': {
          fontSize: 12,
          height: 28,
          borderRadius: '999px',
          borderColor: ColorTokens.secondary500,
          backgroundColor: ColorTokens.secondary500,
          '& .MuiChip-label': {
            padding: '0 12px',
            color: ColorTokens.primary300,
          },
          '& .MuiChip-deleteIcon': {
            color: ColorTokens.primary300,
            fontSize: 14,
          },
          '&:hover': {
            backgroundColor: ColorTokens.secondary500,
          },
        },
      }}
      size='small'
      disabled={props.disabled}
      options={options}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      value={options.filter(l => props.defaultValues.includes(l.id))}
      onChange={(_, v) => {
        if (!props.showOptionForAll) {
          props.onChange(v.map(l => l.id))
        } else {
          const pickedValue = v.length > 0 ? v[v.length - 1] : null

          if (pickedValue && pickedValue.id === '') {
            props.onChange([''])
          } else {
            props.onChange(v.length > 1 ? v.filter(l => l.id !== '').map(l => l.id) : v.map(l => l.id))
          }
        }
      }}
      getOptionLabel={option => option.name}
      renderOption={(props, option, { selected }) => {
        const getTypeBadge = () => {
          if (!option.levelType) {
            return null
          }

          if (option.levelType === 'team') {
            return { label: '[TEAM]', color: '#1976d2' }
          }

          if (option.levelType === 'role') {
            return { label: '[ROLE]', color: '#2e7d32' }
          }

          return null
        }
        const typeBadge = getTypeBadge()

        return (
          <li {...props} key={option.id} style={{ fontSize: 12 }}>
            <AppCheckbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: option.dept * 16 }}
              checked={selected}
              size="small"
            />
            {typeBadge && (
              <span style={{ color: typeBadge.color, fontSize: 12, fontWeight: 600, marginRight: 6 }}>
                {typeBadge.label}
              </span>
            )}
            {option.name}
          </li>
        )
      }}
      slotProps={{
        paper: {
          sx: {
            '& .MuiAutocomplete-listbox': {
              fontSize: 12,
            },
          },
        },
      }}
      style={{ width: '100%', flexWrap: 'wrap' }}
      renderInput={params => (
        <AppTextField
          {...params}
          label={props.label}
          placeholder={props.placeholder}
          sx={{
            fontSize: 12,
            m: 0,
          }}
        />
      )}
    />
  )
}